
import { useState, useEffect, forwardRef, useImperativeHandle, use } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Order } from '../../types/order';
import { OrderRoom } from '../../types/orderRoom';
import { ConferenceHotelRoom, ConferenceHotelRoomResponse } from '../../types/conferenceHotelRoom';
import { APIHOST } from '../../common'; 
import { formatDateFromYYYYMMDD } from '../../../lib/datetime';
import { OrderFormProps } from '../../lib/order';
import {fetchOrder} from '../../lib/order';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useApp } from '../../components/AppContext';


export const roomSelectorErrors = (order: Order) : string[] => {
    const errors: string[] = [];
    if (!order.order_rooms || order.order_rooms.length === 0) {
      errors.push('At least one room must be selected.');
      return errors;
    }

    order.order_rooms.forEach((room, index) => {
      if (room.conference_hotel_room === undefined) {
        errors.push(`Room ${index + 1} is missing a selected hotel room.`);
      }
      if (!room.arrivalDate) {
        errors.push(`Room ${index + 1} is missing an arrival date.`);
      }
      if (!room.departureDate) {
        errors.push(`Room ${index + 1} is missing a departure date.`);
      }
    })
    return errors;
  };

const RoomSelector = ({
  orderId,
  back,
  onSave,
  onStepComplete,
  type
}: OrderFormProps) => {
  const [availableHotelRooms, setAvailableHotelRooms] = useState<ConferenceHotelRoom[]>([]);
  const [conferenceHotel, setConferenceHotel] = useState<any>(null);
  const [order, setOrder] = useState<Order | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<OrderRoom | null>(null);
  const [dialogRoom, setDialogRoom] = useState<Partial<OrderRoom>>({});
  
  const {setSnackbarInput} = useApp();

  const reloadOrderData = async () => {   
    const fetchedOrder = await fetchOrder(orderId);
    if (fetchedOrder) {
      console.debug("FETCHED ORDER IN ROOM SELECTOR:", fetchedOrder);
      setOrder(fetchedOrder);
    }
  };

  const fetchHotelRooms = async () => {
 
      try {
        const authToken = localStorage.getItem('authToken');
        console.log('Fetching conference hotel rooms for order:', orderId);
        const response = await fetch(
          `${APIHOST}/api/conference-hotel-rooms/available?orderId=${orderId}`,
          {
            headers: {
              'Authorization': `Bearer ${authToken}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log('Received hotel rooms data:', data);
     
          setAvailableHotelRooms(data);
          
        } else {
          console.error('Error fetching conference hotel rooms:', response.statusText);
          setAvailableHotelRooms([]);
         
        }
      } catch (error) {
        console.error('Error fetching conference hotel rooms:', error);
        setAvailableHotelRooms([]);
      }
    };

  useEffect(() => { 
    reloadOrderData();
    fetchHotelRooms();
  }, [orderId]);


  // Calculate number of days between arrival and departure
  const calculateDays = (arrivalDate: string, departureDate: string): number => {
    if (!arrivalDate || !departureDate) return 0;
    const arrival = new Date(arrivalDate);
    const departure = new Date(departureDate);
    const timeDiff = departure.getTime() - arrival.getTime();
    return Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
  };

  // Get daily rate for selected hotel room
  const getDailyRate = (conferenceHotelRoomId: string): number => {
    const hotelRoom = availableHotelRooms.find(room => room.documentId === conferenceHotelRoomId);
    return hotelRoom?.dailyRate || 0;
  };

  // Calculate subtotal for a room
  const calculateRoomSubtotal = (room: OrderRoom): number => {
    if (!room.conference_hotel_room || !room.arrivalDate || !room.departureDate) return 0;
    const days = calculateDays(room.arrivalDate, room.departureDate);
    const dailyRate = room.dailyRate;
    return days * dailyRate;
  }

  // Calculate total taxes for a room
  const calculateRoomTaxes = (room: OrderRoom): number => {
    if (!conferenceHotel) return 0;
    const subtotal = calculateRoomSubtotal(room);

  
    let totalTax = 0;
    
    // Add percentage-based tax
    if (conferenceHotel.taxRatePercentage) {
      totalTax += subtotal * (conferenceHotel.taxRatePercentage / 100);
    }
    
    // Add per-night tax
    if (conferenceHotel.taxPerRoomNight) {
      totalTax += calculateDays(room.arrivalDate, room.departureDate) * conferenceHotel.taxPerRoomNight;
    }
    
    return totalTax;
  };

  const calculateRoomTotal = (room: OrderRoom): number => {
      return calculateRoomTaxes(room) + calculateRoomSubtotal(room);
  }

  const deleteRoomFromAPI = async (documentId: string) => {
    const authToken = localStorage.getItem('authToken');
    if (!authToken || !documentId) return;

    try {
      const response = await fetch(`${APIHOST}/api/order-rooms/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Error removing room:', error);
      return false;
    }
  };

  // Dialog handlers
  const openAddRoomDialog = () => {
    setEditingRoom(null);
    setDialogRoom({
      type: 'student',
      arrivalDate: order?.selectedArrivalDate || '',
      departureDate: order?.selectedDepartureDate || '',
      holdStatus: 'held'
    });
    setDialogOpen(true);
  };

  const openEditRoomDialog = (room: OrderRoom) => {
    setEditingRoom(room);
    setDialogRoom({ ...room });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setDialogRoom({});
  };

  const handleDialogRoomChange = (field: keyof OrderRoom, value: any) => {
    // Validate departure date is not before arrival date
    if (field === 'departureDate') {
      if (dialogRoom.arrivalDate && value && new Date(value) <= new Date(dialogRoom.arrivalDate)) {
        return;
      }
    }
    
    // Validate arrival date is not after departure date
    if (field === 'arrivalDate') {
      if (dialogRoom.departureDate && value && new Date(value) >= new Date(dialogRoom.departureDate)) {
        return;
      }
    }

    setDialogRoom(prev => ({ ...prev, [field]: value }));
  };

  const saveRoomFromDialog = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) return;

      const roomData = {
        ...dialogRoom,
        order: order?.documentId
      };
      
      const { id, order_room_occupants, documentId, createdAt, updatedAt, publishedAt, ...cleanRoomData } = roomData;
      const payload = {
        data: cleanRoomData
      };

      const method = editingRoom?.documentId ? 'PUT' : 'POST';
      const url = editingRoom?.documentId 
        ? `${APIHOST}/api/order-rooms/${editingRoom.documentId}`
        : `${APIHOST}/api/order-rooms`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      console.debug(`${editingRoom ? 'Updating' : 'Creating'} room with data:`, payload); 
      if (response.status === 400) {
        const errorData = await response.json();
        console.error('Validation error saving room:', errorData.error.message);
          setSnackbarInput({
            message: errorData.error.message,
            severity: 'error'
          });
          
        return;
      }
      if (response.ok) {
        setSnackbarInput({
          message: 'Room hold added',
          severity: 'success'
        });
        closeDialog();
        onSave && onSave();
        await reloadOrderData();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Failed to save room';
        console.error('Failed to save room:', errorMessage);
        setSnackbarInput({
          message: errorMessage,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error saving room:', error);
    }
  };

  // Get hotel room name for display
  const getHotelRoomName = (conferenceHotelRoomId: string): string => {
    const hotelRoom = availableHotelRooms.find(room => room.documentId === conferenceHotelRoomId);
    return hotelRoom?.name || 'Unknown Room';
  };



  const addRoom = () => {
    openAddRoomDialog();
  };

  const removeRoom = async (roomId: string) => {
    const deleted = await deleteRoomFromAPI(roomId);
    if (deleted) {
      await reloadOrderData();
    } else {
      console.error('Failed to remove room from API');
    }
    
  };


  console.dir(order, { depth: 5 });
console.debug('Rendering RoomSelector with order rooms:', availableHotelRooms);
  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h3">
          Room Selection
        </Typography>
        <Box>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRoom}
          size="small"
        >
          Add Room
        </Button>
        </Box>
      </Box>


      {order?.order_rooms?.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No rooms selected yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Click "Add Room" to start selecting your accommodations
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addRoom}
          >
            Add Your First Room
          </Button>
        </Box>
      ) : (
        order?.order_rooms?.map((room, index) => (
          <Card key={room.documentId || room.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" component="h4">
                  Room {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={() => openEditRoomDialog(room)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => removeRoom(room.documentId || room.id)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Hotel Room
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.conference_hotel_room?.name}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Room Type
                  </Typography>
                  <Chip 
                    label={room.type || 'Student'} 
                    size="small" 
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Check-in
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.arrivalDate ? formatDateFromYYYYMMDD(room.arrivalDate) : 'Not set'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Check-out
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {room.departureDate ? formatDateFromYYYYMMDD(room.departureDate) : 'Not set'}
                  </Typography>
                </Grid>
              </Grid>

              {room.conference_hotel_room && room.arrivalDate && room.departureDate && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {calculateDays(room.arrivalDate, room.departureDate)} nights × ${room.dailyRate}/night
                      ({`tax $${calculateRoomTaxes(room).toFixed(2)}`})
                    </Typography>
                    <Typography variant="h6" fontWeight="600" color="success.main">
                      ${calculateRoomTotal(room).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        ))
      )}
      
      {/* Add Room Button Below Rooms */}
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addRoom}
          size="medium"
        >
          Add Another Room
        </Button>
      </Box>
      
      {/* Grand Total Display */}
      {order?.order_rooms?.some(room => room.conference_hotel_room && room.arrivalDate && room.departureDate) && (
        <Box sx={{ mt: 3}}>
         
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="600">
                Total Accommodation Cost
              </Typography>
              <Typography variant="h4" fontWeight="700" color="success.main">
                ${order?.order_rooms.reduce((total, room) => total + calculateRoomTotal(room), 0).toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total for {order?.order_rooms.filter(room => room.conference_hotel_room).length} room(s)
            </Typography>
        
        </Box>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {order && roomSelectorErrors(order).length > 0 && (
          <Box sx={{ mt: 2, color: 'error.main' }}>
            {roomSelectorErrors(order).map((error, idx) => (
              <Typography key={idx} variant="body2">
                {error}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
      
      {/* Temporary button for testing saveAllRoomData */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={saveAllRoomData}
          color="secondary"
        >
          Save All Room Data (Temp)
        </Button>
      </Box> */}

      {/* Room Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRoom ? 'Edit Room' : 'Add Room'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Hotel Room</InputLabel>
        
                <Select
                  value={dialogRoom.conference_hotel_room || ''}
                  label="Hotel Room"
                  onChange={(e) => handleDialogRoomChange('conference_hotel_room', e.target.value)}
                  disabled={!order?.conference_hotel || availableHotelRooms.length === 0}
                >
                  <MenuItem value="">
                    <em>Select a room</em>
                  </MenuItem>
                  {availableHotelRooms.map((hotelRoom) => (
                    <MenuItem key={hotelRoom.documentId} value={hotelRoom.documentId}>
                      {hotelRoom.name} - ${hotelRoom.dailyRate}/night
                      {hotelRoom.maxOccupants && ` (Max ${hotelRoom.maxOccupants})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Room Type</InputLabel>
                <Select
                  value={dialogRoom.type || 'student'}
                  label="Room Type"
                  onChange={(e) => handleDialogRoomChange('type', e.target.value)}
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="adult">Adult</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Arrival Date"
                type="date"
                value={dialogRoom.arrivalDate || ''}
                onChange={(e) => handleDialogRoomChange('arrivalDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: order?.selectedArrivalDate,
                  max: order?.selectedDepartureDate
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Departure Date"
                type="date"
                value={dialogRoom.departureDate || ''}
                onChange={(e) => handleDialogRoomChange('departureDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: order?.selectedArrivalDate,
                  max: order?.selectedDepartureDate
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={saveRoomFromDialog} 
            variant="contained"
            disabled={!dialogRoom.conference_hotel_room || !dialogRoom.arrivalDate || !dialogRoom.departureDate}
          >
            {editingRoom ? 'Update Room' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Box>
          <Button
            color="primary"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={back} 
            disabled={!back} >
            Back
          </Button>
          <Button
            color="primary"
            size="small"
            variant="contained"
            startIcon={<ArrowForwardIcon fontSize="small" />}
            sx={{px: 4, py: 0.5, mt: 2}}
            onClick={onStepComplete}
            disabled={order ? roomSelectorErrors(order).length > 0 : true}
          >
            {type === 'step' ? 'Next' : 'Save'}
          </Button>
        </Box>
    </Box>
  );
};

export default RoomSelector;